
    import { toRefs, ref } from 'vue';
    import * as turf from '@turf/turf';
    import StreetView from './StreetView.vue';
    import Dialog from '@/components/main/Dialog.vue';
    import BusyLogo from '@/components/BusyLogo.vue';
    import { ScaleControl } from 'mapbox-gl';

    const FLOAT_OFFSET_Y = 120; // The amount the pegman floats above the cursor
    const PEGMAN_TOOLBAR = 29 // The height pegman needs to be set at to align with the bottom of the scalebar


    type Pegman = {
        dragging: boolean // Are we currently dragging pegman?
        paused: boolean // Are we holding pegman still?
        done: boolean // Have we dropped pegman?
        show: boolean // Are we showing streetview?
        lat: number
        lng: number
        bearing: number
        imageUrl: string
    }

    export default {
        components: {
            Dialog,
            StreetView,
            BusyLogo
        },

        data: () => ({
            image: null,
            unavailable: false,
        }),

        setup(props) {
            let { mapValue, scaleControlValue } = toRefs(props);
            let map = null;
            if (mapValue.value.map) { // When we get a MapBox compenent reference
                map = mapValue.value.map;
            } else {
                map = mapValue;
            }
            let scaleControl: ScaleControl = scaleControlValue.value;

            const pegmanSetup: Pegman = {
                dragging: false,
                paused: false,
                done: false,
                show: false,
                lat: 0.0,
                lng: 0.0,
                bearing: 0.0,
                imageUrl: "",
            }

            const pegman = ref(pegmanSetup);
            const panoLoading = ref(false);
            const streetView = ref(null);

            return {
                map,
                pegman,
                panoLoading,
                streetView,
                scaleControl,
            }
        },

        mounted() {
            const streetViewKey = this.$store.getters.getStreetViewKey;
            this.pegmanElement = document.getElementById('pegman');
            this.bottomToolbarElement = document.getElementById('tbdc-bottomtoolbar');
            // Setup mouse events
            this.setMapEvents();
            //// Setup pegman UI location
            this.updatePegmanStanding();

            // Defines a dragging element
            var dragElement = (elmnt: HTMLElement) => {
                if (!elmnt) {
                    return;
                }

                elmnt.onmousedown = (e) => dragMouseDown(e);

                var showPopup = (e: MouseEvent) => {
                    if (document.onmouseup == null) { // Timer might cause this method to fire after the onmouseup event has fired
                        return;
                    }
                    const src = e.srcElement as HTMLElement;
                    if (!(src instanceof HTMLCanvasElement)) { // Only show popup when we are above the map
                        return;
                    }

                    this.endPanoLoad();

                    const location = this.pegman.lat + ',' + this.pegman.lng;
                    const request =
                        'https://maps.googleapis.com/maps/api/streetview/metadata?location=' +
                        location +
                        '&key=' +
                        streetViewKey;
                    fetch(request)
                        .then((response) => response.json())
                        .then((data) => {
                            if (this.pegman.done) {
                                return;
                            }
                            if (data.status !== 'ZERO_RESULTS') {
                                this.startPanoLoad();
                                const imageUrl =
                                    'https://maps.googleapis.com/maps/api/streetview?size=600x300&location=' +
                                    location +
                                    '&key=' +
                                    streetViewKey;
                                this.image = imageUrl;
                                this.pegman.imageUrl = imageUrl;
                                var a = turf.point([this.pegman.lng, this.pegman.lat]);
                                var b = turf.point([data.location.lng, data.location.lat]);
                                this.pegman.bearing = turf.bearing(b, a);
                            } else {
                                this.isUnavailable();
                            }
                        })
                        .catch(() => {
                            this.isUnavailable();
                        })
                }

                var showStreetViewMaybe = () => { // Show streetview if we have an imageUrl
                    if (!this.pegman.done || this.pegman.show) {
                        return;
                    }
                    this.pegman.show = this.pegman.imageUrl !== "";
                    this.image = null;
                    elmnt!.classList.remove("pegman-moving")
                    elmnt!.classList.add("pegman-standing")
                    this.updatePegmanStanding();
                    elmnt!.style.top = "";
                    elmnt!.style.left = "";
                }

                var prevTimer: number?= null;

                var elementDrag = (e: MouseEvent) => { // Update element to be positioned above the cursor
                    e = e || window.event;
                    e.preventDefault();


                    // Center horizontally, float above cursor
                    const elmntWidth = elmnt.offsetWidth;
                    elmnt.style.left = (e.clientX - elmntWidth / 2) + 'px';
                    elmnt.style.top = (e.clientY - FLOAT_OFFSET_Y) + 'px';
                    this.unavailable = false;
                    this.endPanoLoad();
                    this.image = null;

                    // Only retrieve the data when we hold the mouse still
                    if (prevTimer) {
                        clearTimeout(prevTimer);
                    }
                    const timer = setTimeout(() => showPopup(e), 100);
                    prevTimer = timer;
                }

                var closeDragElement = () => {
                    document.onmouseup = null;
                    document.onmousemove = null;
                    this.pegman.dragging = false;
                    this.pegman.done = true;
                    this.endPanoLoad();
                    this.unavailable = false;
                    showStreetViewMaybe();
                }

                var dragMouseDown = (e: MouseEvent) => { // Start dragging the element
                    elmnt!.classList.remove("pegman-standing")
                    elmnt!.classList.add("pegman-moving")
                    this.pegman.dragging = true;
                    this.pegman.done = false;
                    this.pegman.paused = false;
                    this.pegman.imageUrl = "";
                    this.unavailable = false;
                    if (this.pegman.show) {
                        this.closeStreetView();
                    }

                    e = e || window.event;
                    e.preventDefault();
                    // Immediately center and float above cursor
                    const elmntWidth = elmnt.offsetWidth;
                    elmnt.style.left = (e.clientX - elmntWidth / 2) + 'px';
                    elmnt.style.top = (e.clientY - FLOAT_OFFSET_Y) + 'px';
                    elmnt.style.transform = "none";

                    document.onmouseup = closeDragElement;
                    document.onmousemove = elementDrag;
                }

            }
            dragElement.call(this, this.pegmanElement);
        },

        methods: {
            setMapEvents() { // Get the location from the map
                this.map.on('mousemove', (e) => {
                    const newLocation = {
                        lng: e.lngLat.lng,
                        lat: e.lngLat.lat,
                    }
                    if (this.pegman!.lng != newLocation.lng || this.pegman!.lat != newLocation.lat) {
                        this.updateLocation({ lng: newLocation.lng, lat: newLocation.lat });
                    }
                });
                this.map.on('zoom', () => { // Scalebar moves when zooming
                    this.updatePegmanStanding();
                });
                this.map.on('move', () => { // Scalebar moves when moving the map
                    this.updatePegmanStanding();
                });
            },
            updateLocation(lngLat: any) {
                this.pegman!.lng = lngLat.lng;
                this.pegman!.lat = lngLat.lat;
            },
            closeStreetView() {
                if (!this.pegman.show) {
                    return;
                }
                if (this.streetView) {
                    this.streetView.cleanUp();
                }
                this.pegman.show = false;
            },
            updatePegmanStanding() { // Update the style of the bottom toolbar to allign witht the scalebar
                const pegman: HTMLElement = this.pegmanElement as HTMLElement;
                const toolbar: HTMLElement = this.bottomToolbarElement as HTMLElement;
                if (!pegman || !toolbar) return;

                const scaleControlWidth = this.scaleControl._container.offsetWidth;
                const pegmanWidth = pegman.offsetWidth;
                //toolbar.style.width = `${scaleControlWidth + pegmanWidth + 10}px`;

                // Only update pegman when we are not dragging him
                if (pegman.classList.contains('pegman-standing')) {
                    const transformX = scaleControlWidth / 2 + pegmanWidth;
                    pegman.style.transform = `translate(${-transformX}px, ${-PEGMAN_TOOLBAR}px)`;
                }
            },
            isUnavailable() {
                this.unavailable = true;
                this.image = null;
                this.pegman.imageUrl = "";
                this.endPanoLoad();
            },
            startPanoLoad() {
                this.unavailable = false;
                if (!this.panoLoading) {
                    this.panoLoading = true;
                    this.$store.commit("SET_STARTINPROGRESS", null, { root: true });
                }
            },
            endPanoLoad() {
                if (this.panoLoading) {
                    this.panoLoading = false;
                    this.$store.commit("SET_ENDINPROGRESS", null, { root: true });
                }
            }
        },

        watch: {
            mapValue(next) {
                this.map = next;
                this.closeStreetView();
                this.setMapEvents();
            },
            scaleControlValue(next: ScaleControl) {
                this.scaleControl = next;
            }
        },

        props: {
            mapValue: {
                type: Object,
                required: true
            },
            scaleControlValue: {
                type: ScaleControl,
                required: true
            }
        },
    }
